import React, {
  useEffect,
  useState,
} from "react";

import {
  Link,
  NavLink,
  useLocation,
} from "react-router-dom";

import {
  useAppKit,
  useAppKitAccount,
  useDisconnect,
} from "@reown/appkit/react";

/* =========================================================
   NAVBAR
========================================================= */

const Navbar = () => {
  const [
    menuOpen,
    setMenuOpen,
  ] = useState(false);

  const location =
    useLocation();

  /* =========================================================
     REOWN APPKIT
  ========================================================= */

  const {
    open,
  } = useAppKit();

  const {
    address,
    isConnected,
  } = useAppKitAccount();

  const {
    disconnect,
  } = useDisconnect();

  /* =========================================================
     NAVIGATION
  ========================================================= */

  const navItems = [
    {
      name: "Home",
      href: "/",
    },
    {
      name: "Dashboard",
      href: "/dashboard",
    },
    {
      name: "Leaderboard",
      href: "/leaderboard",
    },
    {
      name: "Rewards",
      href: "/rewards",
    },
    {
      name: "Swap",
      href: "/swap",
    },
    {
      name: "History",
      href: "/history",
    },
    {
      name: "Rules & Eligibility",
      href: "/rules",
    },
  ];

  /* =========================================================
     CLOSE MOBILE MENU ON ROUTE CHANGE
  ========================================================= */

  useEffect(
    () => {
      setMenuOpen(false);
    },
    [
      location.pathname,
    ]
  );

  /* =========================================================
     SHORT ADDRESS
  ========================================================= */

  const shortAddress = (
    walletAddress
  ) => {
    if (!walletAddress) {
      return "";
    }

    return `${walletAddress.slice(
      0,
      6
    )}...${walletAddress.slice(
      -4
    )}`;
  };

  /* =========================================================
     CONNECT WALLET
  ========================================================= */

  const handleWallet =
    async () => {
      try {
        await open({
          view: "Connect",
        });
      } catch (error) {
        console.error(
          "Wallet connection error:",
          error
        );
      }
    };

  /* =========================================================
     OPEN ACCOUNT
  ========================================================= */

  const handleAccount =
    async () => {
      try {
        await open({
          view: "Account",
        });
      } catch (error) {
        console.error(
          "Account modal error:",
          error
        );
      }
    };

  /* =========================================================
     DISCONNECT
  ========================================================= */

  const handleDisconnect =
    async () => {
      try {
        await disconnect({
          namespace:
            "eip155",
        });

        try {
          localStorage.removeItem(
            "walletAddress"
          );

          localStorage.removeItem(
            "walletConnected"
          );
        } catch (storageError) {
          console.error(
            "Local storage cleanup error:",
            storageError
          );
        }

        window.dispatchEvent(
          new CustomEvent(
            "walletDisconnected"
          )
        );
      } catch (error) {
        console.error(
          "Wallet disconnect error:",
          error
        );
      }
    };

  /* =========================================================
     MOBILE HELPERS
  ========================================================= */

  const closeMobileMenu =
    () => {
      setMenuOpen(false);
    };

  const openMobileMenu =
    () => {
      setMenuOpen(true);
    };

  const handleMobileWallet =
    () => {
      closeMobileMenu();

      setTimeout(
        async () => {
          await handleWallet();
        },
        260
      );
    };

  const handleMobileAccount =
    () => {
      closeMobileMenu();

      setTimeout(
        async () => {
          await handleAccount();
        },
        260
      );
    };

  const handleMobileDisconnect =
    async () => {
      await handleDisconnect();

      closeMobileMenu();
    };

  /* =========================================================
     ESC KEY
  ========================================================= */

  useEffect(
    () => {
      const handleKeyDown =
        (event) => {
          if (
            event.key ===
            "Escape"
          ) {
            setMenuOpen(false);
          }
        };

      window.addEventListener(
        "keydown",
        handleKeyDown
      );

      return () => {
        window.removeEventListener(
          "keydown",
          handleKeyDown
        );
      };
    },
    []
  );

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <>
      {/* =====================================================
          MAIN NAVBAR
      ===================================================== */}

      <header
        className="
          sticky
          top-0
          z-50
          w-full
          border-b
          border-white/[0.07]
          bg-[#050505]/95
          backdrop-blur-xl
        "
      >
        <div
          className="
            mx-auto
            flex
            h-[68px]
            w-full
            max-w-[1700px]
            items-center
            gap-3
            px-4
            sm:h-[72px]
            sm:px-6
            lg:h-[76px]
            lg:px-5
            xl:px-8
          "
        >
          {/* =================================================
              LOGO
          ================================================= */}

          <Link
            to="/"
            className="
              flex
              min-w-0
              shrink-0
              items-center
              gap-1
              lg:mr-2
            "
          >
            <img
              src="/kindlogo.png"
              alt="KIND Cat Token"
              className="
                h-9
                w-auto
                object-contain
                sm:h-10
                lg:h-11
                xl:h-12
              "
            />

            <span
              className="
                whitespace-nowrap
                text-sm
                font-black
                uppercase
                tracking-[0.06em]
                text-[#ffcc66]
                sm:text-base
                lg:text-[14px]
                xl:text-lg
              "
            >
              KIND CAT
            </span>
          </Link>

          {/* =================================================
              DESKTOP NAVIGATION
          ================================================= */}

          <nav
            className="
              hidden
              min-w-0
              flex-1
              items-center
              justify-center
              gap-0
              lg:flex
              xl:gap-1
            "
          >
            {navItems.map(
              (item) => (
                <NavLink
                  key={
                    item.name
                  }
                  to={
                    item.href
                  }
                  end={
                    item.href ===
                    "/"
                  }
                  className={({
                    isActive,
                  }) =>
                    [
                      "relative shrink-0 whitespace-nowrap rounded-[8px]",
                      "px-2 py-2.5 xl:px-3 xl:py-3",
                      "text-[8px] xl:text-[9px]",
                      "font-semibold uppercase",
                      "tracking-[0.08em] xl:tracking-[0.10em]",
                      "transition-all duration-300",
                      isActive
                        ? "bg-[#ffcc66]/10 text-[#ffcc66]"
                        : "text-[#858585] hover:bg-white/[0.04] hover:text-white",
                    ].join(
                      " "
                    )
                  }
                >
                  {
                    item.name
                  }
                </NavLink>
              )
            )}
          </nav>

          {/* =================================================
              DESKTOP WALLET
          ================================================= */}

          <div
            className="
              hidden
              shrink-0
              items-center
              gap-2
              lg:flex
            "
          >
            {isConnected &&
            address ? (
              <>
                <button
                  type="button"
                  onClick={
                    handleAccount
                  }
                  className="
                    rounded-[8px]
                    border
                    border-[#ffcc66]/20
                    bg-[#ffcc66]/[0.06]
                    px-3
                    py-2.5
                    transition-all
                    duration-300
                    hover:border-[#ffcc66]/40
                    hover:bg-[#ffcc66]/[0.10]
                  "
                >
                  <span
                    className="
                      font-mono
                      text-[9px]
                      font-semibold
                      text-[#ffcc66]
                      xl:text-[10px]
                    "
                  >
                    {shortAddress(
                      address
                    )}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={
                    handleDisconnect
                  }
                  className="
                    rounded-[8px]
                    border
                    border-white/10
                    bg-transparent
                    px-2.5
                    py-2.5
                    text-[8px]
                    font-semibold
                    uppercase
                    tracking-[0.08em]
                    text-[#8d8d8d]
                    transition-all
                    duration-300
                    hover:border-[#ffcc66]/30
                    hover:text-[#ffcc66]
                    xl:px-3
                    xl:text-[9px]
                    xl:tracking-[0.12em]
                  "
                >
                  Disconnect
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={
                  handleWallet
                }
                className="
                  rounded-[8px]
                  border
                  border-[#ffcc66]
                  bg-[#ffcc66]
                  px-3
                  py-2.5
                  text-[9px]
                  font-black
                  uppercase
                  tracking-[0.10em]
                  text-black
                  shadow-[0_0_24px_rgba(255,204,102,0.12)]
                  transition-all
                  duration-300
                  hover:brightness-110
                  xl:px-4
                  xl:text-[10px]
                  xl:tracking-[0.13em]
                "
              >
                Connect Wallet
              </button>
            )}
          </div>

          {/* =================================================
              MOBILE MENU BUTTON
          ================================================= */}

          <button
            type="button"
            onClick={
              openMobileMenu
            }
            className="
              ml-auto
              flex
              h-10
              w-10
              shrink-0
              items-center
              justify-center
              rounded-[10px]
              border
              border-white/[0.08]
              bg-white/[0.025]
              text-[#ffcc66]
              transition-all
              duration-300
              hover:border-[#ffcc66]/25
              hover:bg-[#ffcc66]/[0.06]
              lg:hidden
            "
            aria-label="Open navigation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* =====================================================
          MOBILE LAYER
      ===================================================== */}

      <div
        className={`
          fixed
          inset-0
          z-[999]
          lg:hidden
          ${
            menuOpen
              ? "pointer-events-auto"
              : "pointer-events-none"
          }
        `}
      >
        {/* ===================================================
            BACKDROP
        =================================================== */}

        <div
          role="button"
          tabIndex={-1}
          aria-label="Close navigation"
          onClick={
            closeMobileMenu
          }
          className={`
            absolute
            inset-0
            bg-black/60
            backdrop-blur-[3px]
            transition-opacity
            duration-500
            ease-out
            ${
              menuOpen
                ? "opacity-100"
                : "opacity-0"
            }
          `}
          style={{
            touchAction:
              "pan-y",
          }}
        />

        {/* ===================================================
            MOBILE DRAWER
        =================================================== */}

        <aside
          onClick={(
            event
          ) => {
            event.stopPropagation();
          }}
          className={`
            fixed
            right-0
            top-0
            flex
            h-[100dvh]
            w-[86%]
            max-w-[390px]
            flex-col
            overflow-hidden
            border-l
            border-white/[0.08]
            bg-[#08090a]
            shadow-[-30px_0_80px_rgba(0,0,0,0.55)]
            transition-all
            duration-500
            ease-[cubic-bezier(0.22,1,0.36,1)]
            ${
              menuOpen
                ? "translate-x-0 opacity-100"
                : "translate-x-full opacity-0"
            }
          `}
        >
          {/* BACKGROUND EFFECT */}

          <div
            className="
              pointer-events-none
              absolute
              right-[-120px]
              top-[-100px]
              h-[350px]
              w-[350px]
              rounded-full
              bg-[#ffcc66]/[0.05]
              blur-[100px]
            "
          />

          <div
            className="
              pointer-events-none
              absolute
              bottom-[-120px]
              left-[-100px]
              h-[300px]
              w-[300px]
              rounded-full
              bg-[#ffcc66]/[0.025]
              blur-[100px]
            "
          />

          {/* =================================================
              DRAWER HEADER
          ================================================= */}

          <div
            className="
              relative
              z-10
              flex
              h-[76px]
              shrink-0
              items-center
              justify-between
              border-b
              border-white/[0.07]
              px-5
            "
          >
            <Link
              to="/"
              onClick={
                closeMobileMenu
              }
              className="
                flex
                min-w-0
                shrink-0
                items-center
                gap-3
              "
            >
              <img
                src="/kindlogo.png"
                alt="KIND Cat Token"
                className="
                  h-9
                  w-auto
                  object-contain
                  sm:h-10
                "
              />

              <span
                className="
                  whitespace-nowrap
                  text-sm
                  font-black
                  uppercase
                  tracking-[0.08em]
                  text-[#ffcc66]
                  sm:text-base
                "
              >
                KIND CAT
              </span>
            </Link>

            <button
              type="button"
              onClick={
                closeMobileMenu
              }
              className="
                flex
                h-10
                w-10
                shrink-0
                items-center
                justify-center
                rounded-[10px]
                border
                border-white/[0.08]
                bg-white/[0.025]
                text-[22px]
                font-light
                text-[#999]
                transition-all
                duration-300
                hover:border-[#ffcc66]/25
                hover:bg-[#ffcc66]/[0.05]
                hover:text-[#ffcc66]
              "
              aria-label="Close navigation"
            >
              ×
            </button>
          </div>

          {/* =================================================
              NAV AREA
          ================================================= */}

          <div
            className="
              relative
              z-10
              flex
              min-h-0
              flex-1
              flex-col
              px-4
              pb-3
              pt-5
            "
          >
            <div
              className="
                px-2
              "
            >
              <p
                className="
                  text-[8px]
                  font-black
                  uppercase
                  tracking-[0.22em]
                  text-[#4d4d4d]
                "
              >
                Navigation
              </p>
            </div>

            <nav
              className="
                mt-3
                space-y-1
              "
            >
              {navItems.map(
                (
                  item,
                  index
                ) => {
                  const isActive =
                    item.href === "/"
                      ? location.pathname === "/"
                      : location.pathname === item.href;

                  return (
                    <Link
                      key={
                        item.name
                      }
                      to={
                        item.href
                      }
                      onClick={
                        closeMobileMenu
                      }
                      style={{
                        transitionDelay:
                          menuOpen
                            ? `${70 + index * 40}ms`
                            : "0ms",
                      }}
                      className={`
                        group
                        flex
                        h-[52px]
                        w-full
                        items-center
                        justify-between
                        rounded-[11px]
                        border
                        px-4
                        transition-all
                        duration-500
                        ${
                          menuOpen
                            ? "translate-x-0 opacity-100"
                            : "translate-x-4 opacity-0"
                        }
                        ${
                          isActive
                            ? "border-[#ffcc66]/20 bg-[#ffcc66]/[0.065]"
                            : "border-transparent bg-transparent hover:border-white/[0.06] hover:bg-white/[0.025]"
                        }
                      `}
                    >
                      <div
                        className="
                          flex
                          min-w-0
                          items-center
                          gap-3
                        "
                      >
                        <span
                          className={`
                            h-[6px]
                            w-[6px]
                            shrink-0
                            rounded-full
                            transition-all
                            duration-300
                            ${
                              isActive
                                ? "bg-[#ffcc66] shadow-[0_0_10px_rgba(255,204,102,0.8)]"
                                : "bg-[#303030] group-hover:bg-[#777]"
                            }
                          `}
                        />

                        <span
                          className={`
                            truncate
                            text-[10px]
                            font-bold
                            uppercase
                            tracking-[0.13em]
                            transition-all
                            duration-300
                            ${
                              isActive
                                ? "text-[#ffcc66]"
                                : "text-[#898989] group-hover:text-white"
                            }
                          `}
                        >
                          {
                            item.name
                          }
                        </span>
                      </div>

                      <span
                        className={`
                          ml-3
                          shrink-0
                          text-[15px]
                          transition-all
                          duration-300
                          ${
                            isActive
                              ? "text-[#ffcc66]"
                              : "text-[#343434] group-hover:translate-x-1 group-hover:text-[#888]"
                          }
                        `}
                      >
                        →
                      </span>
                    </Link>
                  );
                }
              )}
            </nav>
          </div>

          {/* =================================================
              MOBILE WALLET
          ================================================= */}

          <div
            className="
              relative
              z-10
              shrink-0
              border-t
              border-white/[0.07]
              bg-[#090a0b]/95
              px-4
              pb-[max(16px,env(safe-area-inset-bottom))]
              pt-4
            "
          >
            {isConnected &&
            address ? (
              <div
                className="
                  rounded-[14px]
                  border
                  border-[#ffcc66]/15
                  bg-[#ffcc66]/[0.035]
                  p-4
                "
              >
                <div
                  className="
                    flex
                    items-center
                    justify-between
                    gap-3
                  "
                >
                  <button
                    type="button"
                    onClick={
                      handleMobileAccount
                    }
                    className="
                      min-w-0
                      flex-1
                      text-left
                    "
                  >
                    <div
                      className="
                        flex
                        items-center
                        gap-2
                      "
                    >
                      <span
                        className="
                          h-2
                          w-2
                          shrink-0
                          rounded-full
                          bg-[#ffcc66]
                          shadow-[0_0_10px_rgba(255,204,102,0.8)]
                        "
                      />

                      <span
                        className="
                          text-[8px]
                          font-black
                          uppercase
                          tracking-[0.17em]
                          text-[#626262]
                        "
                      >
                        Wallet Connected
                      </span>
                    </div>

                    <div
                      className="
                        mt-2
                        truncate
                        font-mono
                        text-[13px]
                        font-semibold
                        text-[#ffcc66]
                      "
                    >
                      {shortAddress(
                        address
                      )}
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={
                      handleMobileAccount
                    }
                    className="
                      shrink-0
                      rounded-[9px]
                      border
                      border-white/[0.08]
                      bg-black/40
                      px-3
                      py-2.5
                      text-[8px]
                      font-black
                      uppercase
                      tracking-[0.12em]
                      text-[#aaa]
                      transition-all
                      duration-300
                      hover:border-[#ffcc66]/25
                      hover:text-[#ffcc66]
                    "
                  >
                    Account
                  </button>
                </div>

                <button
                  type="button"
                  onClick={
                    handleMobileDisconnect
                  }
                  className="
                    mt-3
                    w-full
                    rounded-[9px]
                    border
                    border-white/[0.07]
                    bg-black/30
                    px-4
                    py-2.5
                    text-[8px]
                    font-bold
                    uppercase
                    tracking-[0.13em]
                    text-[#666]
                    transition-all
                    duration-300
                    hover:border-red-500/20
                    hover:bg-red-500/[0.04]
                    hover:text-red-300
                  "
                >
                  Disconnect Wallet
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={
                  handleMobileWallet
                }
                className="
                  group
                  relative
                  flex
                  min-h-[54px]
                  w-full
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-[11px]
                  border
                  border-[#ffcc66]
                  bg-[#ffcc66]
                  px-5
                  text-[10px]
                  font-black
                  uppercase
                  tracking-[0.15em]
                  text-black
                  shadow-[0_10px_35px_rgba(255,204,102,0.10)]
                  transition-all
                  duration-300
                  hover:brightness-110
                  active:scale-[0.985]
                "
              >
                <span
                  className="
                    relative
                    z-10
                  "
                >
                  Connect Wallet
                </span>
              </button>
            )}

            <p
              className="
                mt-3
                text-center
                text-[7px]
                font-semibold
                uppercase
                tracking-[0.2em]
                text-[#343434]
              "
            >
              KIND BONUS RUSH
            </p>
          </div>
        </aside>
      </div>
    </>
  );
};

export default Navbar;